import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Rituals from "@/pages/Rituals";
import Community from "@/pages/Community";
import Journal from "@/pages/Journal";
import Profile from "@/pages/Profile";
import Tracking from "@/pages/Tracking";
import Becoming from "@/pages/Becoming";
import WellnessCreator from "@/pages/WellnessCreator";
import WellnessRitualView from "@/pages/WellnessRitualView";
import Moderation from "@/pages/Moderation";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PricingSuccess from "@/pages/PricingSuccess";
import PricingCancel from "@/pages/PricingCancel";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import SoulLanding from "@/pages/soul/SoulLanding";
import InnerExploration from "@/pages/soul/InnerExploration";
import InnerPractice from "@/pages/soul/InnerPractice";
import RegulationPresence from "@/pages/soul/RegulationPresence";
import SoulComingSoon from "@/pages/soul/SoulComingSoon";
import BreathIndex from "@/pages/soul/breath/BreathIndex";
import BreathPractice from "@/pages/soul/breath/BreathPractice";
import NervousSystem from "@/pages/soul/nervous/NervousSystem";
import Mirror from "@/pages/Mirror";
import ShadowExitBar from "@/components/ShadowExitBar";
import PricingModal from "@/components/PricingModal";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { BrandMark } from "@/components/BrandMark";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f6f1e7", color: "#3a2f24" }}>
        <BrandMark className="text-3xl opacity-70" signOnMount={false} testid="auth-loading-brand" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect the OAuth return synchronously during render (see Emergent
  // Auth playbook) — must run BEFORE ProtectedRoute so we don't bounce
  // to /login while the browser still holds the session_id fragment.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Legal pages are always public so they're reachable from the
          sign-in screen and from anywhere else without an auth wall. */}
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<TermsAndConditions />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/becoming" element={<Becoming />} />
        <Route path="/rituals" element={<Rituals />} />
        <Route path="/soul" element={<SoulLanding />} />
        <Route path="/soul/inner-exploration" element={<InnerExploration />} />
        <Route path="/soul/inner-exploration/:slug" element={<InnerPractice />} />
        <Route path="/soul/regulation-presence" element={<RegulationPresence />} />
        <Route path="/soul/regulation-presence/breath" element={<BreathIndex />} />
        <Route path="/soul/regulation-presence/breath/:slug" element={<BreathPractice />} />
        <Route path="/soul/regulation-presence/nervous-system" element={<NervousSystem />} />
        <Route path="/mirror" element={<Mirror />} />
        <Route path="/community" element={<Community />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/tracking" element={<Tracking />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/wellness" element={<WellnessCreator />} />
        <Route path="/spa" element={<WellnessCreator />} />
        <Route path="/r/:code" element={<WellnessRitualView />} />
        <Route path="/moderation" element={<Moderation />} />
        <Route path="/pricing/success" element={<PricingSuccess />} />
        <Route path="/pricing/cancel" element={<PricingCancel />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <ShadowExitBar />
          <PricingModal />
          <AppRouter />
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
